// import { useMemo, useEffect } from 'react'
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  Box,
  Divider,
  alpha,
  Autocomplete,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material'
import { Link, useParams } from 'react-router-dom'
import { PeopleAltOutlined, Add as AddIcon, EditOutlined as EditIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@emotion/react'
import { useSession } from 'components/Providers'

function RoleList({ roles, districtType, districtTypes, onDistrictTypeChange, onEditClick }) {
  const { id } = useParams()
  const url = '/permission'
  const { t } = useTranslation()
  const theme = useTheme()
  const { appearance } = useSession();

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        minHeight: '80vh',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 2.5, bgcolor: alpha(theme.palette.background.default, 0.3) }}>
        <Autocomplete
          fullWidth
          disableClearable
          variant="outlined"
          options={districtTypes || []}
          getOptionLabel={(option) => option?.name || ''}
          value={districtTypes?.find(type => type?.code === districtType) || null}
          isOptionEqualToValue={(option, value) => option?.code === value?.code}
          onChange={(event, newValue) => {
            if (newValue) {
              onDistrictTypeChange(newValue.code);
            }
          }}
          sx={{ mt: 1, mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('District Type')}
              placeholder={t('Select District Type...')}
            />
          )}
        />

        <Button
          onClick={() => onEditClick?.()}
          fullWidth
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          sx={{ fontWeight: 'bold' }}
        >
          {t('New Role')}
        </Button>
      </Box>

      <Divider />

      <List sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'background.paper' }}>
        {roles.map(role => {
          const isSelected = id == role.id;
          return (
            <ListItem key={role.id} disablePadding>
              <ListItemButton
                component={Link}
                to={[url, role.id].join('/')}
                selected={isSelected}
                sx={{
                  borderRadius: 0,
                  py: 1.5,
                  mb: 0.5,
                  borderLeft: isSelected ? '4px solid' : '4px solid transparent',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'primary.50' : 'transparent',
                  '&.Mui-selected': { bgcolor: 'primary.50' },
                  '&.Mui-selected:hover': { bgcolor: 'primary.100' },
                  '&:hover': { bgcolor: appearance === 'dark' ? 'grey.800' : 'grey.100' }
                }}
              >
                <ListItemText
                  primary={role.name}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? 'primary.700' : 'text.primary'
                  }}
                  secondaryTypographyProps={{ fontSize: '0.75rem', mt: 0.5 }}
                />
                {role.shared && (
                  <Tooltip title={t('This role is shared across multiple campuses')} placement="top" arrow>
                    <PeopleAltOutlined fontSize="small" sx={{ color: isSelected ? 'primary.main' : 'text.disabled', cursor: 'help', mr: 0.5 }} />
                  </Tooltip>
                )}
                <Tooltip title={t('Edit role')} placement="top" arrow>
                  <IconButton
                    size="small"
                    onClick={e => { e.preventDefault(); e.stopPropagation(); onEditClick?.(role) }}
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </Paper>
  )
}

export default RoleList